import {
  INCIDENTS_LOAD,
  INCIDENTS_ADD_NEW,
  INCIDENTS_CLEAR,
  INCIDENTS_LOAD_EVENT_TYPES,
  INCIDENTS_CALENDAR_CHANGE_DATE,
  INCIDENTS_CALENDAR_CHANGE_VIEW,
  INCIDENTS_CALENDAR_RESET,
  INCIDENTS_SET_KEYWORD,
  INCIDENTS_UPDATE,
  INCIDENTS_DELETE,
} from '../../actionTypes/incidents';
import moment from 'moment';
import produce from 'immer';
import sortItemByKind from '../../utils/sortItemByKind';
import {
  incidentTypeConfig,
  IncidentTypeKey,
  ViewsEnum,
} from '../../../pages/Incidents/utils/constants';
import { SortingKindEnum } from '../../../utils/interfaces';
import { Order } from '../../../utils/apollo/graphqlSchema';
import { IIncident, IIncidentType } from '../../../pages/Incidents/interfaces';
import { PayloadFromCreator } from '../../utils/types';
import {
  addNewIncidentAction,
  changeDateAction,
  changeViewAction,
  deleteIncidentAction,
  loadIncidentsAction,
  loadIncidentTypesAction,
  setIncidentsKeywordAction,
  updateIncidentAction,
} from '../../actions/incidents';

type IIncidentWithAnalysis = {
  analysisId?: string;
} & IIncident;

export interface IncidentsState {
  calendar: {
    date: Date;
    view: ViewsEnum;
  };
  incidents: IIncident[];
  incidentsSorting: {
    enabled: boolean;
    kind: SortingKindEnum;
    type: Order;
  };
  incidentTypes: Record<string, IIncidentType>;
  currentIncident: IIncidentWithAnalysis | null;
  keyword: string;
}

const defaultState: IncidentsState = {
  calendar: {
    date: moment().toDate(),
    view: ViewsEnum.MONTH,
  },
  incidents: [],
  incidentsSorting: {
    enabled: false,
    kind: SortingKindEnum.DATE,
    type: Order.Desc,
  },
  incidentTypes: {},
  currentIncident: null,
  keyword: '',
};

const addIncidentTypeAndTitle = (
  incident: IIncident,
  incidentType?: IIncidentType,
) => {
  return {
    ...incident,
    title: incident.name,
    type: {
      ...incidentType,
      ...incident.type,
    },
  };
};

// TODO: @Brett move to dictionary reducer, and remove unnessary redux actions and states
// for example: modal go to redux-modals. change keyword, calendar, etc to useState.
// XXX additional note from @Asen: right now we still keep track of incidents in two redux places:
// * state.dictionary[DitionaryTypeEnum.CalendarIncidents]
// * state.incidents (which is this)
// We should indeed consolidate these two (as suggested by @Brett) but using the standard modals without other
// changes might be an issue as the Callout used to display the popup tends to stay on
// top of our standard modals.
export default (state = defaultState, action: any) =>
  produce(state, (draft: any) => {
    switch (action.type) {
      case INCIDENTS_LOAD: {
        const { incidents, sorted } = action.payload as PayloadFromCreator<
          typeof loadIncidentsAction
        >;
        const parsedIncidents = incidents.map((incident: IIncident) => {
          const currentType: IIncidentType =
            state.incidentTypes[incident.type.id];
          return addIncidentTypeAndTitle(incident, currentType);
        });
        if (sorted) {
          draft.incidents = sortItemByKind(
            parsedIncidents,
            state.incidentsSorting.kind,
            state.incidentsSorting.type,
          );
          draft.incidentsSorting.enabled = sorted;
          return;
        }
        draft.incidents = parsedIncidents;
        return;
      }
      case INCIDENTS_ADD_NEW: {
        const newIncident = action.payload as PayloadFromCreator<
          typeof addNewIncidentAction
        >;
        const newIncidentType: IIncidentType =
          state.incidentTypes[newIncident.type.id];
        const newIncidents = [
          ...state.incidents,
          addIncidentTypeAndTitle(newIncident, newIncidentType),
        ];
        if (state.incidentsSorting.enabled) {
          draft.incidents = sortItemByKind(
            newIncidents,
            state.incidentsSorting.kind,
            state.incidentsSorting.type,
          );
          return;
        }
        draft.incidents = newIncidents;
        return;
      }
      case INCIDENTS_UPDATE: {
        const updatedIncident = action.payload as PayloadFromCreator<
          typeof updateIncidentAction
        >;
        const updatedIncidents = state.incidents.map((stateIncident) => {
          if (stateIncident.id === updatedIncident.id) {
            const newIncidentType: IIncidentType =
              state.incidentTypes[updatedIncident.type.id];
            return addIncidentTypeAndTitle(updatedIncident, newIncidentType);
          } else {
            return stateIncident;
          }
        });
        if (state.incidentsSorting.enabled) {
          draft.incidents = sortItemByKind(
            updatedIncidents,
            state.incidentsSorting.kind,
            state.incidentsSorting.type,
          );
          return;
        }
        draft.incidents = updatedIncidents;
        return;
      }
      case INCIDENTS_DELETE: {
        const { incidentId } = action.payload as PayloadFromCreator<
          typeof deleteIncidentAction
        >;
        const updatedIncidents = state.incidents.filter(
          (v) => v.id !== incidentId,
        );
        draft.incidents = updatedIncidents;
        return;
      }
      case INCIDENTS_CLEAR: {
        draft.incidents = [];
        return;
      }
      case INCIDENTS_LOAD_EVENT_TYPES: {
        const incidentTypesArray = action.payload as PayloadFromCreator<
          typeof loadIncidentTypesAction
        >;
        const incidentTypesObject = incidentTypesArray.reduce(
          (obj: any, { id, name }: any) => {
            const { icon, color } = incidentTypeConfig[name as IncidentTypeKey];
            obj[id] = {
              id,
              name,
              icon,
              color,
            };
            return obj;
          },
          {},
        );
        draft.incidentTypes = incidentTypesObject;
        return;
      }

      case INCIDENTS_CALENDAR_CHANGE_DATE: {
        const date = action.payload as PayloadFromCreator<
          typeof changeDateAction
        >;
        draft.calendar.date = date;
        return;
      }
      case INCIDENTS_CALENDAR_CHANGE_VIEW: {
        const view = action.payload as PayloadFromCreator<
          typeof changeViewAction
        >;
        draft.calendar.view = view;
        return;
      }
      case INCIDENTS_CALENDAR_RESET: {
        draft.calendar = defaultState.calendar;
        return;
      }
      case INCIDENTS_SET_KEYWORD: {
        const keyword = action.payload as PayloadFromCreator<
          typeof setIncidentsKeywordAction
        >;
        draft.keyword = keyword;
        return;
      }
    }
  });
